import { useCallback, useState } from "react";
import { Clock, Loader2, MessageSquare, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { listChatSessions } from "@/services/chatService";
import type { SessionSummary } from "@/types/authService";

interface ChatHistoryMenuProps {
  // Reopen the chosen session as a tab. May be async (fetches the transcript);
  // the menu keeps the clicked row spinning until it resolves, then closes.
  onSelect: (summary: SessionSummary) => Promise<void> | void;
  // Session ids currently open as tabs — used to mark them in the list.
  openSessionIds: string[];
}

const sessionLabel = (s: SessionSummary): string =>
  s.title || s.last_message_preview || "Untitled chat";

const ChatHistoryMenu = ({ onSelect, openSessionIds }: ChatHistoryMenuProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [pendingId, setPendingId] = useState<string | null>(null);

  // Refetch the list every time the menu opens so just-evicted / just-created
  // chats show up without a manual refresh.
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) return;
    setLoading(true);
    listChatSessions()
      .then((res) => setSessions(res.sessions))
      .catch((err) => {
        console.warn("Failed to load chat history:", err);
        setSessions([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = useCallback(
    async (summary: SessionSummary) => {
      setPendingId(summary.session_id);
      try {
        await onSelect(summary);
        setOpen(false);
      } catch (err) {
        console.warn("Failed to open chat from history:", err);
      } finally {
        setPendingId(null);
      }
    },
    [onSelect],
  );

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" title="Chat history">
                <Clock className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Chat history</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Chat history</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : sessions.length === 0 ? (
          <div className="py-6 text-center text-xs text-muted-foreground">
            No past chats yet
          </div>
        ) : (
          <ScrollArea className="max-h-72">
            {sessions.map((s) => {
              const label = sessionLabel(s);
              const isOpen = openSessionIds.includes(s.session_id);
              const isPending = pendingId === s.session_id;
              return (
                <DropdownMenuItem
                  key={s.session_id}
                  // Keep the menu open while the transcript fetch is in flight
                  // so the row can show its loading spinner.
                  onSelect={(e) => {
                    e.preventDefault();
                    void handleSelect(s);
                  }}
                  className="flex items-center gap-2 py-2"
                >
                  {isPending ? (
                    <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
                  ) : (
                    <MessageSquare className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  )}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex-1 truncate text-sm">{label}</span>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-xs">
                        <p className="break-words">{label}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {isOpen && <Check className="h-4 w-4 flex-shrink-0 text-primary" />}
                </DropdownMenuItem>
              );
            })}
          </ScrollArea>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ChatHistoryMenu;
